import type { PresenceEntry, View } from "@valuemap/core";
import styles from "./Live.module.css";

/**
 * Who is in the map right now (brief 8.1): one avatar per connection, with their name, role and
 * view in the tooltip. A polite live region reads out the list whenever it changes, so people
 * using a screen reader hear arrivals and departures.
 */

const VIEW_LABEL: Record<View, string> = {
  edit: "Edit",
  quantify: "Quantify",
  prioritise: "Prioritise",
  present: "Present",
};
const ROLE_LABEL = { owner: "Owner", editor: "Editor", viewer: "Viewer" } as const;

/** White initials on each of these meet 4.5:1 contrast. */
const COLOURS = ["#07638d", "#123342", "#8a4f0c", "#5b4b8a", "#9b2c4e", "#2f6b3d"];
const MAX_SHOWN = 8;

function colourFor(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return COLOURS[h % COLOURS.length] ?? "#07638d";
}

/** One or two initials: first and last word. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export function PresenceBar({
  participants,
  me,
}: {
  participants: readonly PresenceEntry[];
  me: string | null;
}) {
  if (participants.length === 0) return null;
  // You first, then everyone else by name.
  const sorted = [...participants].sort((a, b) =>
    a.id === me ? -1 : b.id === me ? 1 : a.name.localeCompare(b.name),
  );
  const shown = sorted.slice(0, MAX_SHOWN);
  const more = sorted.length - shown.length;
  const names = sorted.map((p) => (p.id === me ? `${p.name} (you)` : p.name));
  return (
    <div className={styles.presence}>
      <ul className={styles.avatars} aria-label="People in this value map">
        {shown.map((p) => {
          const label = `${p.name}${p.id === me ? " (you)" : ""}, ${ROLE_LABEL[p.role]}, ${VIEW_LABEL[p.view]}${p.leading ? ", leading" : ""}`;
          return (
            <li
              key={p.id}
              className={`${styles.avatar} ${p.leading ? styles.leading : ""}`}
              style={{ background: colourFor(p.name) }}
              title={label}
              aria-label={label}
            >
              <span aria-hidden="true">{initials(p.name)}</span>
            </li>
          );
        })}
        {more > 0 && (
          <li className={`${styles.avatar} ${styles.more}`} aria-label={`and ${more} more`}>
            +{more}
          </li>
        )}
      </ul>
      <span className="visually-hidden" role="status" aria-live="polite">
        {participants.length === 1
          ? "Only you are in this value map."
          : `${participants.length} people in this value map: ${names.join(", ")}.`}
      </span>
    </div>
  );
}
